import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, EffectCoverflow, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import 'swiper/css/a11y';
import artistsBackground from '../assets/images/5b3d6411-5236-4a4d-8810-d851b9e0de49.jpg';
import jonathanImage from '../assets/images/Artists/jonathan.jpeg';
import yuvalImage from '../assets/images/Artists/yuval_behar.jpeg';
import yftahImage from '../assets/images/Artists/yftah.jpeg';
import yoavImage from '../assets/images/Artists/yoav_dagan.jpg';

// ============================================
// CONFIGURATION
// ============================================
// To enable desktop swiper carousel (when you have more artists):
// 1. Change ENABLE_DESKTOP_SWIPER to true (line 39)
// 2. Optionally uncomment effect="coverflow" (line 194) for 3D effect
// ============================================

// Artists data - easy to add/remove artists
const ARTISTS = [
  {
    id: 1,
    name: 'Jonathan Hazan',
    subtitle: 'Comb, the Yellow Crane, Blind Matador',
    image: jonathanImage,
    testimonial: 'Working with Dor was an absolute blast from start to finish. I came in with some wild ideas and Dor not only obliged - he doubled down. The process was pure joy, with routine updates - pics and videos - and more ideas that came flowing as the guitar took shape. The result is a singular, unique piece with a distinct voice and ergonomic design that plays like nothing I have had before.',
    role: 'Vitæ Guitar Artist'
  },
  {
    id: 2,
    name: 'Yuval Behar',
    subtitle: 'Blind Matador, Kaliko, Karniband, Saar Twito - Moonlight',
    image: yuvalImage,
    testimonial: 'My search for the right 5-string bass led me to Vitæ Guitars. After trying nearly every option I could find in Israel, I realized I needed an instrument built specifically around my needs. Dor Biton brought that vision to life with exceptional craftsmanship, incredible attention to detail, and close collaboration throughout every stage of the design and build process. The Yuta B5 Auctor is everything I hoped for in terms of comfort, playability, feel, and sound. It inspires me every time I pick it up and has genuinely changed the way I play. I couldn\'t be happier with the instrument or with the experience of creating it together with Dor.',
    role: 'Vitæ Guitars artist'
  },
  {
    id: 3,
    name: 'Yftah Livny',
    subtitle: 'Vitæ Guitars Customer',
    image: yftahImage,
    testimonial: 'I met Dor while bringing in my guitars for setups. After I casually mentioned wanting a custom build, he offered to do it and we turned it into a full collaboration, designing every detail together (woods, scale lengths, pickups, controls, inlays). He kept me updated step by step with progress photos and decisions. The result was an incredible-sounding, great-looking 8-string—Dor\'s first.',
    role: 'Vitæ Guitars Customer'
  },
  {
    id: 4,
    name: 'Yoav Dagan',
    subtitle: 'Vitæ Guitars Customer',
    image: yoavImage,
    testimonial: 'After watching Dor\'s attention to detail in wood, hardware, and fretwork, I had to have one of his builds. My first Vitæ is a 6-string Huta that plays effortlessly—every note rings clean, no buzz, no fight. Then, as collectors do, I needed another: a nasty 7-string with a fan-fretted pale moon ebony board, the same playability, and Jupyter pickups that chug hard and clean up beautifully.',
    role: 'Vitæ Guitars Customer'
  }
];

const TestimonialsSection = () => {
  const parallaxRef = useRef(null);
  const sectionRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedArtists, setExpandedArtists] = useState({});

  const toggleExpanded = (id) => {
    setExpandedArtists((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Configuration: Enable/disable swiper for desktop
  const ENABLE_DESKTOP_SWIPER = false; // Set to true when you have more artists

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1000);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current && sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrolled = window.pageYOffset;
        const sectionTop = scrolled + rect.top;
        const sectionHeight = rect.height;
        const windowHeight = window.innerHeight;

        // Apply parallax when section is in view - reduced rate on mobile
        if (rect.top < windowHeight && rect.bottom > 0) {
          const rate = (scrolled - sectionTop) * (window.innerWidth >= 768 ? 0.7 : 0.3);
          parallaxRef.current.style.transform = `translateY(${rate}px)`;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Custom styles for swiper
  const swiperStyles = `
    .artists-swiper {
      padding: 20px 0 60px 0 !important;
    }
    .artists-swiper .swiper-slide {
      background-position: center;
      background-size: cover;
      height: auto !important;
      border-radius: 12px;
      transition: all 0.3s ease;
    }
    .mobile-artists-swiper .swiper-slide {
      width: 320px !important;
      height: 500px !important;
      margin-left: 10px !important;
      margin-right: 10px !important;
    }
    .artists-swiper .swiper-pagination-bullet {
      background: rgba(255, 159, 102, 0.4) !important;
      width: 12px !important;
      height: 12px !important;
      margin: 0 6px !important;
      opacity: 1 !important;
    }
    .artists-swiper .swiper-pagination-bullet-active {
      background: #FF7A33 !important;
      transform: scale(1.1) !important;
      box-shadow: 0 0 8px rgba(255, 122, 51, 0.5) !important;
    }
    .artists-swiper .swiper-button-next,
    .artists-swiper .swiper-button-prev {
      color: #FF7A33 !important;
      transform: scale(0.8);
    }
    .artists-swiper .swiper-button-next:hover,
    .artists-swiper .swiper-button-prev:hover {
      transform: scale(0.9);
    }
  `;

  const ArtistCard = ({ artist }) => {
    const isExpanded = !!expandedArtists[artist.id];

    return (
    <div className="bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-md rounded-xl overflow-hidden shadow-2xl border border-white/10 hover:border-theme-primary-500/50 transition-all duration-500 flex flex-col w-full h-full">
      {/* Image Section - Main Focus */}
      <div className="relative overflow-hidden group">
        <div className="aspect-square w-full">
          <img
            src={artist.image}
            alt={artist.name}
            className="w-full h-full object-cover object-top transform group-hover:scale-105 transition-transform duration-700"
          />
          {/* Gradient Overlay */}  
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
        </div>
        {/* Artist Name Overlay on Image */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
          <h3 className="text-lg md:text-xl font-bold text-white mb-1">{artist.name}</h3>
          <p className="text-theme-primary-400 text-xs font-medium">{artist.subtitle}</p>
        </div>
      </div>

      {/* Testimonial Section */}
      <div className="p-4 md:p-5 flex flex-col justify-between relative flex-grow">
        {/* Testimonial Text */}
        <div className="relative z-10 flex-grow flex flex-col">
          <blockquote className={`text-gray-200 text-sm leading-relaxed mb-2 italic flex-grow ${isMobile && !isExpanded ? 'line-clamp-4' : ''}`}>
            <span className="text-theme-primary-500 text-xl mr-3">
              <i className="fas fa-quote-left"></i>
            </span>
            {artist.testimonial}
            <span className="text-theme-primary-500 text-xl ml-3">
              <i className="fas fa-quote-right"></i>
            </span>
          </blockquote>

          {isMobile && (
            <button
              type="button"
              onClick={() => toggleExpanded(artist.id)}
              className="flex items-center gap-1.5 text-theme-primary-400 text-xs font-semibold mb-3 -mt-1"
            >
              {isExpanded ? 'Show less' : 'Read more'}
              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-[10px]`}></i>
            </button>
          )}

          {/* Decorative Line */}
          <div className="h-0.5 w-12 bg-gradient-to-r from-theme-primary-500 to-theme-primary-600 rounded-full mb-3"></div>

          {/* Additional Info */}
          <div className="flex items-center gap-2 text-gray-400">
            <i className="fas fa-guitar text-theme-primary-500 text-sm"></i>
            <p className="text-xs">{artist.role}</p>
          </div>
        </div>
      </div>
    </div>
    );
  };

  return (
    <section ref={sectionRef} id="artists" className="py-32 relative overflow-hidden bg-black">
      <style dangerouslySetInnerHTML={{ __html: swiperStyles }} />
      {/* Background Image with Parallax Effect */}
      <div
        ref={parallaxRef}
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{
          backgroundImage: `url(${artistsBackground})`,
          height: '120%',
          top: '-10%'
        }}
      />

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-transparent" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6">
        <div className="text-center mb-20">
          <span className="text-theme-primary-400 tracking-[0.2em] uppercase text-sm font-bold mb-2 block">Community</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Artists & Players</h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto font-light">
            Musicians who trust Vitæ Guitars to bring their sonic vision to life.
          </p>
        </div>

        {/* Desktop View - Swiper */}
        {!isMobile && (
          <div className="max-w-6xl mx-auto">
            <Swiper
              modules={[Navigation, Pagination, A11y]}
              grabCursor={true}
              centeredSlides={true}
              slidesPerView={1}
              spaceBetween={32}
              initialSlide={1}
              loop={false}
              breakpoints={{
                768: { slidesPerView: 2 },
              }}
              pagination={{ clickable: true }}
              navigation={true}
              className="artists-swiper !pb-16"
            >
              {ARTISTS.map((artist) => (
                <SwiperSlide key={artist.id} className="!h-auto">
                  <ArtistCard artist={artist} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        {/* Mobile Swiper Carousel */}
        {isMobile && (
          <Swiper
            modules={[Navigation, Pagination, A11y]}
            effect="slide"
            grabCursor={true}
            centeredSlides={true}
            slidesPerView="auto"
            spaceBetween={0}
            initialSlide={0}
            allowTouchMove={true}
            pagination={{ clickable: true }}
            className="artists-swiper mobile-artists-swiper !pb-12"
          >
            {ARTISTS.map((artist) => (
              <SwiperSlide key={artist.id} className="!h-auto">
                <ArtistCard artist={artist} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
